"use client"

import React, { useState } from "react"
import { HttpTypes } from "@medusajs/types"
import { convertToLocale } from "@lib/util/money"
import { updateLineItem, deleteLineItem } from "@lib/data/cart"
import Link from "next/link"

export default function CartView({
  cart,
}: {
  cart: HttpTypes.StoreCart | null
}) {
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Kupon Promo State di Cart
  const [couponCode, setCouponCode] = useState("")
  const [appliedPromo, setAppliedPromo] = useState<any | null>(() => {
    return cart?.promotions && cart.promotions.length > 0 ? cart.promotions[0] : null
  })
  const [discountTotal, setDiscountTotal] = useState<number>(cart?.discount_total || 0)
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)
  const [couponMsg, setCouponMsg] = useState<{ text: string; isError: boolean } | null>(null)

  const items = cart?.items || []
  const subtotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
  const grandTotal = Math.max(0, subtotal - discountTotal)

  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!couponCode.trim() || !cart?.id) return
    setIsApplyingCoupon(true)
    setCouponMsg(null)
    try {
      const res = await fetch("/api/cart/promotions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cartId: cart.id, promoCode: couponCode.trim() }),
      })
      const data = await res.json()
      if (!res.ok) {
        setCouponMsg({ text: data.error || "Kupon tidak valid.", isError: true })
      } else {
        setAppliedPromo(data.promotions?.[0] || { code: couponCode.trim().toUpperCase() })
        setDiscountTotal(data.discount_total || 0)
        setCouponMsg({ text: `Kupon "${couponCode.trim().toUpperCase()}" berhasil dipasang!`, isError: false })
        setCouponCode("")
      }
    } catch {
      setCouponMsg({ text: "Gagal memproses kupon.", isError: true })
    } finally {
      setIsApplyingCoupon(false)
    }
  }

  const handleRemoveCoupon = async () => {
    if (!appliedPromo || !cart?.id) return
    setIsApplyingCoupon(true)
    setCouponMsg(null)
    try {
      const res = await fetch("/api/cart/promotions", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cartId: cart.id, promoCode: appliedPromo.code }),
      })
      const data = await res.json()
      if (res.ok) {
        setAppliedPromo(null)
        setDiscountTotal(0)
        setCouponMsg({ text: "Kupon telah dihapus.", isError: false })
      }
    } catch {
      setCouponMsg({ text: "Gagal menghapus kupon.", isError: true })
    } finally {
      setIsApplyingCoupon(false)
    }
  }

  const handleQuantityChange = async (lineId: string, newQty: number) => {
    if (newQty < 1) {
      handleRemoveItem(lineId)
      return
    }
    setError(null)
    setUpdatingId(lineId)
    try {
      await updateLineItem({ lineId, quantity: newQty })
    } catch (err: any) {
      setError(err.message || "Gagal memperbarui jumlah produk.")
    } finally {
      setUpdatingId(null)
    }
  }

  const handleRemoveItem = async (lineId: string) => {
    setError(null)
    setUpdatingId(lineId)
    try {
      await deleteLineItem(lineId)
    } catch (err: any) {
      setError(err.message || "Gagal menghapus produk.")
    } finally {
      setUpdatingId(null)
    }
  }

  if (!items.length) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 sm:p-12">
          <div className="w-20 h-20 bg-stone-100 text-stone-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <span className="text-xs font-bold tracking-widest text-[#9d7b56] uppercase block mb-1">
            KERANJANG BELANJA
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 mb-2">
            Keranjang Belanja Anda Masih Kosong
          </h1>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-8 leading-relaxed">
            Yuk pilih tas atau aksesoris Gotosovie favoritmu untuk melengkapi aktivitas harianmu.
          </p>
          <Link
            href="/"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-black text-white text-xs font-semibold tracking-widest uppercase hover:bg-gray-800 transition-all shadow-md"
          >
            Mulai Belanja &rarr;
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Keranjang Belanja</h1>
        <p className="text-sm text-gray-600 mt-1">
          Periksa kembali produk pilihan Anda sebelum melanjutkan ke formulir pemesanan & pembayaran.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 font-bold ml-4">
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 items-start">
        {/* KOLOM KIRI: DAFTAR PRODUK */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-black text-white text-sm font-bold flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </span>
                <h2 className="text-lg font-bold font-serif text-gray-900 tracking-wide uppercase">
                  Daftar Produk ({items.length} Item)
                </h2>
              </div>
              <Link
                href="/"
                className="text-xs font-semibold text-[#9d7b56] hover:text-black transition-colors uppercase tracking-wider hidden sm:inline"
              >
                + Tambah Produk Lain
              </Link>
            </div>

            <div className="divide-y divide-gray-100">
              {items.map((item) => {
                const isUpdating = updatingId === item.id
                return (
                  <div key={item.id} className="py-5 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
                    <div className="flex gap-4 items-center">
                      <div className="w-20 h-20 bg-stone-100 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100 relative">
                        {item.thumbnail ? (
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                            Tas
                          </div>
                        )}
                        {isUpdating && (
                          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0">
                        <Link
                          href={`/store/products/${item.product_handle}`}
                          className="font-serif font-bold text-sm text-gray-900 hover:text-[#9d7b56] transition-colors line-clamp-1"
                        >
                          {item.title}
                        </Link>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Varian: <span className="text-gray-700 font-medium">{item.variant_title || "Standard"}</span>
                        </p>
                        <p className="text-xs font-semibold text-gray-900 mt-1">
                          {convertToLocale({ amount: item.unit_price })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-6 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-50">
                      {/* Quantity Controls */}
                      <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-stone-50/50">
                        <button
                          type="button"
                          disabled={isUpdating || item.quantity <= 1}
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-stone-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors text-base"
                        >
                          -
                        </button>
                        <span className="w-10 text-center text-xs font-semibold text-gray-900">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          disabled={isUpdating}
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-stone-200 disabled:opacity-30 transition-colors text-base"
                        >
                          +
                        </button>
                      </div>

                      {/* Total Item Price */}
                      <div className="text-right min-w-[100px]">
                        <span className="font-bold text-sm text-gray-900 block">
                          {convertToLocale({ amount: item.unit_price * item.quantity })}
                        </span>
                        <button
                          type="button"
                          disabled={isUpdating}
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-[11px] text-red-500 hover:text-red-700 transition-colors mt-0.5"
                        >
                          Hapus
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="pt-6 mt-6 border-t border-gray-100 flex items-center justify-between">
              <Link
                href="/"
                className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-black uppercase tracking-wider transition-colors"
              >
                &larr; Lanjut Belanja
              </Link>
            </div>
          </div>
        </div>

        {/* KOLOM KANAN: RINGKASAN BELANJA (STICKY) */}
        <div className="sticky top-6">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7">
            <h2 className="text-lg font-bold font-serif text-gray-900 pb-4 mb-4 border-b border-gray-100">
              Ringkasan Belanja
            </h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-600 text-xs">
                <span>Total Harga ({items.reduce((s, i) => s + i.quantity, 0)} Barang)</span>
                <span className="font-semibold text-gray-900">
                  {convertToLocale({ amount: subtotal })}
                </span>
              </div>

              {/* Kupon Promo Section */}
              <div className="py-3 border-t border-b border-gray-100 my-2">
                {appliedPromo ? (
                  <div className="flex items-center justify-between p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-[10px]">
                        %
                      </span>
                      <div>
                        <p className="font-bold text-emerald-900 uppercase">
                          {appliedPromo.code}
                        </p>
                        <p className="text-emerald-700 text-[11px]">
                          Hemat {convertToLocale({ amount: discountTotal })}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isApplyingCoupon}
                      onClick={handleRemoveCoupon}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold px-1"
                    >
                      Hapus
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleApplyCoupon} className="space-y-1.5">
                    <label className="block text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                      Kode Promo / Kupon
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Ketik kode kupon..."
                        className="flex-1 px-3 py-1.5 text-xs uppercase font-medium rounded-xl border border-gray-300 focus:outline-none focus:ring-1 focus:ring-black"
                      />
                      <button
                        type="submit"
                        disabled={isApplyingCoupon || !couponCode.trim()}
                        className="px-3 py-1.5 bg-black hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 text-white text-xs font-semibold rounded-xl uppercase transition-colors"
                      >
                        {isApplyingCoupon ? "..." : "Pakai"}
                      </button>
                    </div>
                    {couponMsg && (
                      <p
                        className={`text-[11px] ${
                          couponMsg.isError ? "text-rose-600" : "text-emerald-600"
                        }`}
                      >
                        {couponMsg.text}
                      </p>
                    )}
                  </form>
                )}
              </div>

              {discountTotal > 0 && (
                <div className="flex justify-between text-emerald-600 text-xs font-semibold">
                  <span>Diskon Kupon</span>
                  <span>- {convertToLocale({ amount: discountTotal })}</span>
                </div>
              )}

              <div className="flex justify-between text-gray-600 text-xs">
                <span>Estimasi Ongkir</span>
                <span className="text-gray-500 text-xs">
                  Dihitung di langkah checkout
                </span>
              </div>

              <div className="p-3 bg-stone-50 rounded-xl border border-stone-200 text-xs text-gray-600 space-y-1 mt-2">
                <p className="font-semibold text-gray-800">Pengiriman se-Indonesia</p>
                <p className="text-gray-500 leading-relaxed">
                  Pilihan kurir (SiCepat, JNE, J&T, dll.) dan tarif otomatis akan tampil saat mengisi alamat pengiriman.
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200 flex justify-between items-baseline">
                <span className="font-bold text-sm text-gray-900">Total Belanja</span>
                <span className="font-bold text-xl text-rose-700 font-serif">
                  {convertToLocale({ amount: grandTotal })}
                </span>
              </div>
            </div>

            {/* Tombol Lanjut ke Checkout */}
                        <Link
                          href="/checkout"
                          className="w-full mt-6 py-4 rounded-full bg-black hover:bg-gray-800 text-white font-semibold text-xs tracking-widest uppercase transition-all shadow-md flex items-center justify-center gap-2 text-center"
                        >
                          Lanjut ke Checkout ({convertToLocale({ amount: grandTotal })}) &rarr;
                        </Link>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Pembayaran Aman via QRIS & Transfer Bank
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
